import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError } from '@/lib/utils';
import { User, Lock, Phone, Eye, EyeSlash } from '@phosphor-icons/react';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(phone, password);
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'artisan' || user.role === 'facility_manager') navigate('/feed');
      else navigate('/');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E8DDD2] to-[#D6C9B8] flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block">
            <h1 className="font-heading text-3xl font-black text-[#1A4D2E]">Naija<span className="text-[#E86A33]">Fix</span></h1>
          </Link>
          <p className="text-[#5C5C5C] mt-2 font-body">Welcome back. Sign in to continue.</p>
        </div>

        <div className="bg-white rounded-xl border border-[#D4C5B3] p-6 shadow-sm">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm" data-testid="login-error">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 block">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={20} />
                <input
                  data-testid="login-phone-input"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="08012345678"
                  className="w-full bg-white border border-[#D4C5B3] rounded-lg pl-10 pr-4 py-3 text-[#1C1C1C] focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E] outline-none transition-all font-body"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5C] mb-2 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]" size={20} />
                <input
                  data-testid="login-password-input"
                  type={showPw ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full bg-white border border-[#D4C5B3] rounded-lg pl-10 pr-10 py-3 text-[#1C1C1C] focus:ring-2 focus:ring-[#1A4D2E]/20 focus:border-[#1A4D2E] outline-none transition-all font-body"
                  required
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C5C5C]">
                  {showPw ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#1A4D2E] hover:bg-[#133922] text-white font-bold py-3 rounded-lg transition-all duration-200 disabled:opacity-50 font-body"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-[#5C5C5C] font-body">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#E86A33] font-bold hover:underline" data-testid="go-to-register">Create one</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
